/* Copyright (c) 2026 Xperi. All Rights Reserved. */

import type { CarouselTemplate } from "../../model/enums";
import type { TCarouselItem } from "../../model/types";
import type {
  TCarouselRailPaginationState,
  TTilePaginationState
} from "../utility/carouselPagination";
import { DEFAULT_CAROUSELS_REFRESH_INTERVAL_SECS } from "../../config";
import { PlayerCarouselFocusedField } from "./playerCarouselFocusedField";

/**
 * Redux state for the PlayerCarousel feature slice.
 *
 * Mirrors the homescreen slice's pagination + TTL model so the
 * player-screen carousels can be lazily paginated rail-by-rail and
 * tile-by-tile, with CRITICAL retry and program-boundary refresh.
 *
 * Also preserves the legacy single-rail fields (`items`, `title`,
 * `template`) for backward compatibility with the existing
 * PlayerCarousel View layer.
 */
export interface PlayerCarouselState {
  // ── Pagination (homescreen-aligned) ───────────────────────────────────
  /** Cursor-paginated list of carousel rails for the Player screen. */
  carouselsPagination: TCarouselRailPaginationState;
  /** Per-rail offset-paginated tile state, keyed by carouselId. */
  tilePagination: Record<string, TTilePaginationState>;

  // ── Focus ─────────────────────────────────────────────────────────────
  /** Index of the currently focused carousel rail. */
  focusedCarouselIndex: number;
  /** Index of the focused tile within each carousel rail (sparse). */
  focusedTileIndices: Partial<Record<number, number>>;

  // ── TTL / Refresh ─────────────────────────────────────────────────────
  /** Refresh interval (seconds) returned by the latest API response. */
  refreshIntervalSecs: number;
  /** Epoch ms of the last successful fetch — used for TTL checks. */
  lastFetchTimestamp: number;

  // ── Legacy single-rail fields (backward compatibility) ────────────────
  /** Mapped feed items ready for the Carousel component. Empty until loaded. */
  items: TCarouselItem[];
  /** Display title of the carousel rail. */
  title: string;
  /** Tile template that controls aspect ratio for the rail. */
  template: CarouselTemplate | null;
  /** Whether a fetch is currently in flight. */
  isLoading: boolean;
  /** Error message if the last fetch failed. Empty string if no error. */
  error: string;
  /** Which field is currently focused in the player controls overlay. */
  focusedField: PlayerCarouselFocusedField;
}

/** Payload returned by the legacy loadPlayerCarousel thunk on success. */
export interface PlayerCarouselPayload {
  items: TCarouselItem[];
  title: string;
  template: CarouselTemplate | null;
}

export const INITIAL_STATE: PlayerCarouselState = {
  // Pagination
  carouselsPagination: {
    items: [],
    pageCursor: null,
    isLoading: false,
    error: "",
    errorCode: ""
  },
  tilePagination: {},
  focusedField: PlayerCarouselFocusedField.ACTIONS,

  // Focus
  focusedCarouselIndex: 0,
  focusedTileIndices: {},

  // TTL / Refresh
  refreshIntervalSecs: DEFAULT_CAROUSELS_REFRESH_INTERVAL_SECS,
  lastFetchTimestamp: 0,

  // Legacy single-rail
  items: [],
  title: "",
  template: null,
  isLoading: false,
  error: ""
};
